import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const CpdRegistrationsManagement = () => {
    const [registrations, setRegistrations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [governingBodyFilter, setGoverningBodyFilter] = useState('');
    const [pagination, setPagination] = useState({ page: 1, limit: 25, total: 0, totalPages: 0 });
    const [selectedRegistration, setSelectedRegistration] = useState(null);

    const headers = {
        'Authorization': `Bearer ${localStorage.getItem('adminToken')}`,
        'Content-Type': 'application/json'
    };

    useEffect(() => {
        fetchRegistrations();
    }, [pagination.page, search, statusFilter, governingBodyFilter]);

    const fetchRegistrations = async () => {
        try {
            setLoading(true);
            const params = new URLSearchParams({
                page: pagination.page,
                limit: pagination.limit
            });
            if (search) params.append('search', search);
            if (statusFilter) params.append('status', statusFilter);
            if (governingBodyFilter) params.append('governingBody', governingBodyFilter);

            const res = await axios.get(`${API_URL}/admin/cpd-registrations?${params}`, { headers });
            if (res.data.success) {
                setRegistrations(res.data.data || []);
                setPagination(prev => ({
                    ...prev,
                    total: res.data.pagination?.total || 0,
                    totalPages: res.data.pagination?.pages || 0
                }));
            }
        } catch (err) {
            toast.error('Failed to load CPD registrations');
        } finally {
            setLoading(false);
        }
    };

    const handleStatusUpdate = async (id, newStatus) => {
        try {
            const res = await axios.patch(
                `${API_URL}/admin/cpd-registrations/${id}/status`,
                { status: newStatus },
                { headers }
            );
            if (res.data.success) {
                toast.success('Registration status updated');
                fetchRegistrations();
            }
        } catch (err) {
            toast.error('Failed to update status');
        }
    };

    const handleDownload = async () => {
        try {
            const res = await axios.get(`${API_URL}/admin/cpd-registrations/download?format=csv`, {
                headers,
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `cpd-registrations-${new Date().toISOString().split('T')[0]}.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            toast.success('Download started');
        } catch (err) {
            toast.error('Failed to download registrations');
        }
    };

    const getStatusBadgeClass = (status) => {
        switch (status) {
            case 'confirmed': return 'bg-success';
            case 'rejected': return 'bg-danger';
            default: return 'bg-warning text-dark';
        }
    };

    const getPageNumbers = () => {
        const { page, totalPages } = pagination;
        if (totalPages <= 7) {
            return Array.from({ length: totalPages }, (_, i) => i + 1);
        }
        if (page <= 4) {
            return [1, 2, 3, 4, 5, '...', totalPages];
        }
        if (page >= totalPages - 3) {
            return [1, '...', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
        }
        return [1, '...', page - 1, page, page + 1, '...', totalPages];
    };

    return (
        <div className="container-fluid py-4">
            <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
                <div>
                    <h2 className="h4 fw-bold mb-1">
                        <i className="bi bi-mortarboard me-2 text-success"></i>
                        CPD Registrations
                    </h2>
                    <p className="text-muted mb-0">Respiratory Care Month 2026 Webinar Series</p>
                </div>
                <div className="d-flex gap-2">
                    <button className="btn btn-success" onClick={handleDownload}>
                        <i className="bi bi-download me-2"></i>DOWNLOAD REGISTRATIONS
                    </button>
                </div>
            </div>

            {/* Filters */}
            <div className="row g-3 mb-4">
                <div className="col-lg-4 col-md-6">
                    <input
                        type="text"
                        className="form-control"
                        placeholder="Search name, email, phone, MoMo ID..."
                        value={search}
                        onChange={(e) => { setSearch(e.target.value); setPagination(p => ({ ...p, page: 1 })); }}
                    />
                </div>
                <div className="col-lg-2 col-md-3">
                    <select
                        className="form-select"
                        value={statusFilter}
                        onChange={(e) => { setStatusFilter(e.target.value); setPagination(p => ({ ...p, page: 1 })); }}
                    >
                        <option value="">All Statuses</option>
                        <option value="pending">Pending</option>
                        <option value="confirmed">Confirmed</option>
                        <option value="rejected">Rejected</option>
                    </select>
                </div>
                <div className="col-lg-2 col-md-3">
                    <select
                        className="form-select"
                        value={governingBodyFilter}
                        onChange={(e) => { setGoverningBodyFilter(e.target.value); setPagination(p => ({ ...p, page: 1 })); }}
                    >
                        <option value="">All Bodies</option>
                        <option value="AHPC">AHPC</option>
                        <option value="NMC">NMC</option>
                    </select>
                </div>
                <div className="col-lg-2 col-md-3">
                    <button
                        className="btn btn-outline-secondary w-100"
                        onClick={() => {
                            setSearch('');
                            setStatusFilter('');
                            setGoverningBodyFilter('');
                            setPagination(p => ({ ...p, page: 1 }));
                        }}
                    >
                        <i className="bi bi-x-circle me-1"></i>Clear
                    </button>
                </div>
            </div>

            {/* Summary */}
            <div className="mb-3 text-muted small">
                Showing {registrations.length} of {pagination.total} registrations
            </div>

            {/* Registrations Table */}
            {loading ? (
                <div className="text-center py-5"><div className="spinner-border text-success" role="status"></div></div>
            ) : registrations.length === 0 ? (
                <div className="text-center py-5 text-muted">
                    <i className="bi bi-inbox fs-1 d-block mb-2"></i>
                    <p>No CPD registrations found</p>
                </div>
            ) : (
                <div className="table-responsive">
                    <table className="table table-hover align-middle">
                        <thead className="table-light">
                            <tr>
                                <th>#</th>
                                <th>Name</th>
                                <th>Body</th>
                                <th>Profession</th>
                                <th>CRCE</th>
                                <th>Member</th>
                                <th>Fee</th>
                                <th>Payment</th>
                                <th>Status</th>
                                <th>Date</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {registrations.map((r, idx) => (
                                <tr key={r.id}>
                                    <td>{(pagination.page - 1) * pagination.limit + idx + 1}</td>
                                    <td>
                                        {r.first_name} {r.middle_name ? `${r.middle_name} ` : ''}{r.last_name}
                                    </td>
                                    <td>{r.governing_body}</td>
                                    <td>{r.profession_other || r.profession}</td>
                                    <td>{r.wants_crce ? 'Yes' : 'No'}</td>
                                    <td>{r.is_arca_member ? 'Yes' : 'No'}</td>
                                    <td>{r.crce_fee > 0 ? `$${parseFloat(r.crce_fee).toFixed(2)}` : 'FREE'}</td>
                                    <td>
                                        <span className={`badge ${r.payment_status === 'paid' ? 'bg-success' : r.payment_status === 'pending' ? 'bg-warning text-dark' : 'bg-secondary'}`}>
                                            {r.payment_status || 'N/A'}
                                        </span>
                                    </td>
                                    <td>
                                        <span className={`badge ${getStatusBadgeClass(r.status)}`}>
                                            {r.status}
                                        </span>
                                    </td>
                                    <td>{r.created_at ? new Date(r.created_at).toLocaleDateString() : '-'}</td>
                                    <td>
                                        <button
                                            className="btn btn-sm btn-outline-primary me-1 mb-1"
                                            onClick={() => setSelectedRegistration(r)}
                                            title="View details"
                                        >
                                            <i className="bi bi-eye"></i>
                                        </button>
                                        <select
                                            className="form-select form-select-sm d-inline-block w-auto"
                                            value={r.status}
                                            onChange={(e) => handleStatusUpdate(r.id, e.target.value)}
                                            title="Update status"
                                        >
                                            <option value="pending">Pending</option>
                                            <option value="confirmed">Confirmed</option>
                                            <option value="rejected">Rejected</option>
                                        </select>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Pagination */}
            {!loading && pagination.totalPages > 1 && (
                <nav className="mt-4">
                    <ul className="pagination justify-content-center">
                        <li className={`page-item ${pagination.page === 1 ? 'disabled' : ''}`}>
                            <button
                                className="page-link"
                                onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}
                                disabled={pagination.page === 1}
                            >
                                Previous
                            </button>
                        </li>
                        {getPageNumbers().map((n, i) => (
                            <li key={i} className={`page-item ${n === pagination.page ? 'active' : ''} ${n === '...' ? 'disabled' : ''}`}>
                                {n === '...' ? (
                                    <span className="page-link">...</span>
                                ) : (
                                    <button className="page-link" onClick={() => setPagination(p => ({ ...p, page: n }))}>
                                        {n}
                                    </button>
                                )}
                            </li>
                        ))}
                        <li className={`page-item ${pagination.page === pagination.totalPages ? 'disabled' : ''}`}>
                            <button
                                className="page-link"
                                onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}
                                disabled={pagination.page === pagination.totalPages}
                            >
                                Next
                            </button>
                        </li>
                    </ul>
                </nav>
            )}

            {/* Detail Modal */}
            {selectedRegistration && (
                <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} onClick={() => setSelectedRegistration(null)}>
                    <div className="modal-dialog modal-dialog-centered modal-lg" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">CPD Registration Details</h5>
                                <button type="button" className="btn-close" onClick={() => setSelectedRegistration(null)}></button>
                            </div>
                            <div className="modal-body">
                                <div className="row g-3">
                                    <div className="col-md-6"><strong>Name:</strong> {selectedRegistration.first_name} {selectedRegistration.middle_name ? `${selectedRegistration.middle_name} ` : ''}{selectedRegistration.last_name}</div>
                                    <div className="col-md-6"><strong>Gender:</strong> {selectedRegistration.gender}</div>
                                    <div className="col-md-6"><strong>Governing Body:</strong> {selectedRegistration.governing_body}</div>
                                    <div className="col-md-6"><strong>Profession:</strong> {selectedRegistration.profession_other || selectedRegistration.profession}</div>
                                    <div className="col-md-6"><strong>PIN/Registration Number:</strong> {selectedRegistration.pin}</div>
                                    <div className="col-md-6"><strong>Email:</strong> {selectedRegistration.email}</div>
                                    <div className="col-md-6"><strong>Phone:</strong> {selectedRegistration.phone}</div>
                                    <div className="col-md-6"><strong>Wants CRCE:</strong> {selectedRegistration.wants_crce ? 'Yes' : 'No'}</div>
                                    {selectedRegistration.wants_crce ? (
                                        <>
                                            <div className="col-md-6"><strong>ARCA Member:</strong> {selectedRegistration.is_arca_member ? 'Yes' : 'No'}</div>
                                            {selectedRegistration.membership_id && <div className="col-md-6"><strong>ARCA Membership ID:</strong> {selectedRegistration.membership_id}</div>}
                                            <div className="col-md-6"><strong>CRCE Fee:</strong> {selectedRegistration.crce_fee > 0 ? `$${parseFloat(selectedRegistration.crce_fee).toFixed(2)} USD` : 'FREE'}</div>
                                            <div className="col-md-6"><strong>Certificate:</strong> {selectedRegistration.certificate_included ? 'Included FREE' : 'No'}</div>
                                            <div className="col-md-6"><strong>Payment Status:</strong> <span className={`badge ${selectedRegistration.payment_status === 'paid' ? 'bg-success' : selectedRegistration.payment_status === 'pending' ? 'bg-warning text-dark' : 'bg-secondary'}`}>{selectedRegistration.payment_status || 'N/A'}</span></div>
                                            {selectedRegistration.momo_transaction_id && <div className="col-md-6"><strong>MoMo Transaction ID:</strong> {selectedRegistration.momo_transaction_id}</div>}
                                        </>
                                    ) : (
                                        <div className="col-md-6"><strong>MoMo Transaction ID:</strong> {selectedRegistration.momo_transaction_id || 'N/A'}</div>
                                    )}
                                    <div className="col-md-6"><strong>Status:</strong> <span className={`badge ${getStatusBadgeClass(selectedRegistration.status)}`}>{selectedRegistration.status}</span></div>
                                    <div className="col-md-6"><strong>Submitted:</strong> {selectedRegistration.created_at ? new Date(selectedRegistration.created_at).toLocaleString() : '-'}</div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-secondary" onClick={() => setSelectedRegistration(null)}>Close</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CpdRegistrationsManagement;
